import React, { Component } from "react";
import PropTypes from "prop-types";
import { deleteApi, fetchApi, saveApi } from "../../utils/DatabaseHelper";
import { tableColums, tableDefaultSort } from "../../config/TableViewConfig";
import TableHeader from "./TableHeader";
import TableBody from "./TableBody";
import LoadingSpinner from "../../utils/Loading";
import isEqual from "lodash.isequal";
import ConfirmationDialog from "../ConfirmationDialog";
import { toast } from "react-toastify";
import { toggleArchiveStatus } from "../Archive/ArchiveHelper";
import ArchiveDialog from "../ArchiveDialog";
import logger from "../../logging/logger";
import { MITARBEITER, MITARBEITER_ARCHIVE } from "../../utils/TableConstanten";
import { downloadMitarbeiterZip } from "../../utils/ExportHelper";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import HistoryList from "../Forms/HistoryList";
import { dedupeMetaFields } from "../../utils/metaFieldsHelper";
import EditableMetaCell from "./EditableMetaCell";
import moment from "moment";

const TABLE_VIEW_PREFIX = "tableMetaView:";

// Persönliche Tabellen-Ansicht pro Endpoint aus localStorage: { hidden, order } der Meta-Keys
function readTableViewConfig(apiEndpoint) {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(TABLE_VIEW_PREFIX + apiEndpoint),
    );
    if (!parsed || typeof parsed !== "object") return null;
    return {
      hidden: new Set(Array.isArray(parsed.hidden) ? parsed.hidden : []),
      order: Array.isArray(parsed.order) ? parsed.order : [],
    };
  } catch (e) {
    return null;
  }
}

function writeTableViewConfig(apiEndpoint, config) {
  try {
    localStorage.setItem(
      TABLE_VIEW_PREFIX + apiEndpoint,
      JSON.stringify({ hidden: [...config.hidden], order: config.order }),
    );
  } catch (e) {
    // Quota/Privacy-Mode: Ansicht gilt dann nur für die Session
  }
}

// Angezeigte Meta-Spalten: Nutzer-Reihenfolge (unbekannte Keys hinten anhängen), Versteckte raus
function buildMetaColumns(metaFields, config) {
  const defs = metaFields.map((f) => ({
    key: f.key,
    dbName: `meta.${f.key}`,
    label: f.label,
    fieldFirmenId: f.firmenId,
  }));
  const byKey = new Map(defs.map((c) => [c.key, c]));
  if (!config) return defs;
  const order = [
    ...config.order.filter((k) => byKey.has(k)),
    ...metaFields.map((f) => f.key).filter((k) => !config.order.includes(k)),
  ];
  return order
    .map((k) => byKey.get(k))
    .filter((c) => !config.hidden.has(c.key));
}

const isMetaColumn = (column) =>
  typeof column?.dbName === "string" && column.dbName.startsWith("meta.");

/**
 * Wandelt eine gespeicherte E-Mail-Fehlermeldung in eine lesbare
 * HTML-Darstellung um. Unterstützt sowohl rohe Strings als auch
 * serialisierte JSON-Objekte von nodemailer.
 */
function EmailLogError({ error }) {
  if (!error) {
    return null;
  }

  let parsed = null;
  try {
    parsed = JSON.parse(error);
  } catch (e) {
    parsed = null;
  }

  if (parsed && typeof parsed === "object") {
    const cleanedResponse = (parsed.response || "")
      .replace(/\u003c[^\u003e]*\u003e/g, "")
      .trim();

    return (
      <div className="mt-2">
        {parsed.message && <p className="mb-2">{parsed.message}</p>}
        {cleanedResponse && (
          <p className="mb-2">
            <strong>Server-Antwort:</strong> {cleanedResponse}
          </p>
        )}
        {parsed.attempts !== undefined && (
          <p className="mb-1">
            <strong>Versuche:</strong> {parsed.attempts}
          </p>
        )}
        {parsed.timestamp && (
          <p className="mb-0">
            <strong>Zeitpunkt:</strong>{" "}
            {moment(parsed.timestamp).format("DD.MM.YYYY HH:mm")}
          </p>
        )}
      </div>
    );
  }

  return <p className="mb-0 mt-2">{error}</p>;
}

class Table extends Component {
  defaultValues = {
    currentPage: 0,
    totalPages: 0,
    orderBy: "",
    sortOrder: "",
    loading: true,
    error: null,
    columns: [],
    data: [],
    showHistoryOnly: false,
    historyData: [],
  };

  state = {
    currentPage: 0,
    totalPages: 0,
    orderBy: undefined,
    sortOrder: undefined,
    loading: true,
    error: null,
    columns: [],
    data: [],
    hasMore: false,
    isConfirmationDialogOpen: false,
    isArchivDialogOpen: false,
    showHistoryOnly: false,
    historyData: [],
    emailLogDetail: null,
    isEmailLogDetailOpen: false,
    emailLogConfirm: null,
    metaFields: [],
    metaConfig: null,
    columnMenuOpen: false,
  };

  dragMetaKey = null;

  // Ref für den Scroll-Container
  scrollContainerRef = React.createRef();

  componentDidMount() {
    global.emitter.on("REFRESH_TABLE_DATA", this.fetchData);
    global.emitter.on("RESET", this.handleReset);

    // Event-Listener am Container statt am Window anhängen
    // Dies wird nach dem ersten Rendering geschehen
    this.attachScrollListener();

    this.fetchData();
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps, this.props)) {
      this.fetchData(false);
    }

    // Bei Bedarf Scroll-Listener neu anhängen
    this.attachScrollListener();
  }

  componentWillUnmount() {
    // Ein spät auflösender Fetch der alten Seite darf nach dem Seitenwechsel
    // die csvQuery/daten der neuen Seite nicht mehr überschreiben
    this.unmounted = true;
    // window.removeEventListener("scroll", this.handleScroll);
    this.detachScrollListener();

    global.emitter.off("REFRESH_TABLE_DATA", this.fetchData);
    global.emitter.off("RESET", this.handleReset);
  }

  // Hilfsmethode zum Anhängen des Scroll-Listeners
  attachScrollListener = () => {
    const scrollContainer = document.querySelector(".card-body");
    if (scrollContainer && !this._scrollAttached) {
      scrollContainer.addEventListener("scroll", this.handleScroll);
      this._scrollAttached = true;
    }
  };
  // Hilfsmethode zum Entfernen des Scroll-Listeners
  detachScrollListener = () => {
    const scrollContainer = document.querySelector(".card-body");
    if (scrollContainer && this._scrollAttached) {
      scrollContainer.removeEventListener("scroll", this.handleScroll);
      this._scrollAttached = false;
    }
  };

  handleScroll = (event) => {
    const { loading, hasMore } = this.state;
    if (loading || !hasMore) return;

    // Der scrollContainer ist jetzt das Event-Target
    const scrollContainer = event.target;

    // Berechnung basierend auf dem Container statt dem Dokument
    const scrollTop = scrollContainer.scrollTop;
    const scrollHeight = scrollContainer.scrollHeight;
    const clientHeight = scrollContainer.clientHeight;

    // Debug-Ausgabe (optional)
    // console.log("Scroll:", {scrollTop, scrollHeight, clientHeight});

    // Prüfen, ob der Benutzer nahe am Ende des Containers ist
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      this.setState({ loading: true }, () => this.fetchData(true));
    }
  };

  handleReset = (cb) => {
    this.setState(
      { ...this.defaultValues, showPagination: false, showFilter: false },
      () => cb(),
    );
  };

  fetchData = (append = false) => {
    const { apiEndpoint, filters } = this.props;
    // logger.log("fetch", filters);
    let defaultSorting = tableDefaultSort[apiEndpoint] ?? {};
    let query = {
      ...filters,
      orderBy: this.state.orderBy ?? defaultSorting.orderBy ?? undefined,
      sortOrder: this.state.sortOrder ?? defaultSorting.sortOrder ?? undefined,
      currentPage: append ? this.state.currentPage : 0,
    };

    // apiEndpoint z.B. "/mitarbeiter", "/mitarbeiter/123", "/mitarbeiter/123/archive"
    if (apiEndpoint.includes("archive")) {
      // Archive-Route
      query = { ...query, is_archived: true };
    } else if (apiEndpoint.includes("mitarbeiter")) {
      // Alle anderen Mitarbeiter-Routen
      query = { ...query, is_archived: false };
    }

    let columns = (tableColums[apiEndpoint] || []).filter(
      (col) =>
        col.filter !== true &&
        (!col.superAdminOnly || global.auth?.user?.rolle === "superadmin"),
    );
    if (this.props.embedded) {
      //apiEndpoint = report/mitarbeiter_details/:id soll werden - "/:id"
      columns = tableColums[apiEndpoint.split("/").slice(0, 2).join("/")];
    }
    const hasMetaColumns =
      apiEndpoint === MITARBEITER || apiEndpoint === MITARBEITER_ARCHIVE;
    logger.log("fetch", apiEndpoint, query, append);
    fetchApi(apiEndpoint, "", query)
      .then(async (resp) => {
        if (this.unmounted) return;
        // Spalten-Scope über ALLE geladenen Zeilen (bei Append inkl. Vorseiten):
        // der Firmenfilter der Tabelle steuert damit auch die Meta-Spalten
        const loadedRows = append
          ? [...this.state.data, ...resp.data]
          : resp.data;
        // State-Konfig zuerst: in Privacy-/Quota-Fällen (localStorage-Write schlägt fehl)
        // gilt die per applyMetaConfig gesetzte Session-Konfiguration
        const metaConfig =
          this.state.metaConfig ?? readTableViewConfig(apiEndpoint);
        // CSV-Export umfasst in der Regel mehr Zeilen/Firmen als die geladene
        // Seite (limit=ALL) — Spaltenbasis daher ungescoopt; der Backend-CSV-Pfad
        // begrenzt selbst auf die Firmen der Export-Zeilen
        const [metaFields, csvMetaFields] = await Promise.all([
          this.getMetaFields(apiEndpoint, loadedRows),
          this.getAllMetaFields(apiEndpoint),
        ]);
        // Filterfelder mitführen (Ticket #354): gleicher Scope wie die Spalten
        this.props.onMetaFilterFields?.(metaFields);
        const metaColumns = buildMetaColumns(metaFields, metaConfig);
        this.attachMetaInlineEdit(apiEndpoint, metaColumns);
        this.setState(
          (prevState) => ({
            ...prevState,
            data: append ? [...prevState.data, ...resp.data] : resp.data,
            loading: false,
            columns: [...columns, ...metaColumns],
            metaFields,
            csvMetaFields,
            metaConfig,
            orderBy: query.orderBy,
            sortOrder: query.sortOrder,
            currentPage: resp.currentPage + 1,
            totalPages: resp.totalPages,
            hasMore: resp.currentPage + 1 < resp.totalPages,
          }),
          // () => logger.log(this.state.data)
        );
        if (
          !this.props.embedded &&
          typeof this.props.setCsvQuery === "function"
        ) {
          const csvQuery = { ...query };
          // metaCols nur bei gelungener CSV-Basis setzen (fehlgeschlagener Fetch,
          // leerer Meta-Spalten-Vorrat → Backend-Default statt leerem Schnitt);
          // leere Liste bei vollständig versteckten Feldern ist korrekt (kein schnitt)
          if (hasMetaColumns && csvMetaFields.length > 0) {
            csvQuery.metaCols = buildMetaColumns(csvMetaFields, metaConfig)
              .map((c) => c.key)
              .join(",");
          }
          this.lastCsvQuery = { apiEndpoint, query: csvQuery };
          this.props.setCsvQuery(this.lastCsvQuery);
        }
      })
      .catch((error) => {
        this.setState({ error, loading: false, metaFields: [] });
      });
  };

  // showInList-Metafelder, gescoopt auf die Firmen der geladenen Zeilen
  // (global + diese Firmen; Shadowing serverseitig aufgelöst — wie im CSV-Export)
  async getMetaFields(apiEndpoint, rows) {
    if (apiEndpoint !== MITARBEITER && apiEndpoint !== MITARBEITER_ARCHIVE) {
      return [];
    }
    try {
      const firmenIds = [
        ...new Set(rows.map((r) => r.firmenId).filter((id) => id != null)),
      ];
      const data = await fetchApi(
        "metaSettings/visible",
        firmenIds.length > 0 ? "?firmenIds=" + firmenIds.join(",") : "",
      );
      return data.data || [];
    } catch (err) {
      logger.log("Meta-Spalten konnten nicht geladen werden", err);
      return [];
    }
  }

  // Alle showInList-Metafelder ungescoopt — nur als Spaltenbasis für den
  // CSV-Export, dessen Row-Umfang (limit=ALL) über die geladene Seite hinausgeht
  async getAllMetaFields(apiEndpoint) {
    if (apiEndpoint !== MITARBEITER && apiEndpoint !== MITARBEITER_ARCHIVE) {
      return [];
    }
    try {
      const data = await fetchApi("metaSettings");
      const fields = (data.data || []).filter((f) => f.showInList === true);
      // Shadowing: ohne Dedupe gäbe es doppelte Spalten (gleicher Key)
      return dedupeMetaFields(fields);
    } catch (err) {
      logger.log("CSV-Meta-Spalten konnten nicht geladen werden", err);
      return [];
    }
  }

  // Wert nach Inline-Edit in der Zeile aktualisieren (ohne Tabellen-Reload);
  // leerer String = Wert gelöscht, bleibt als leerer Wert sichtbar
  updateMetaValue = (rowId, key, value) => {
    this.setState((prev) => ({
      data: prev.data.map((r) =>
        r.id === rowId
          ? { ...r, meta: { ...(r.meta ?? {}), [key]: value } }
          : r,
      ),
    }));
  };

  // Inline-Edit-Renderer an Meta-Spalten hängen; nötig in fetchData UND in
  // applyMetaConfig, weil buildMetaColumns dort frische Column-Objekte liefert
  attachMetaInlineEdit(apiEndpoint, metaColumns) {
    // nur in der Mitarbeiter-Übersicht (nicht Archiv) und nur
    // mit meta_data-Berechtigung; sonst bleiben die Zellen reiner Text
    if (
      apiEndpoint === MITARBEITER &&
      global.auth.permissions?.meta_data?.edit
    ) {
      for (const column of metaColumns) {
        column.render = (value, row) => (
          <EditableMetaCell
            value={value}
            entityId={row.id}
            rowFirmenId={row.firmenId}
            fieldFirmenId={column.fieldFirmenId}
            metaKey={column.key}
            onSaved={(savedEntityId, savedValue) =>
              this.updateMetaValue(savedEntityId, column.key, savedValue)
            }
          />
        );
      }
    }
    return metaColumns;
  }

  // Aktive Ansichts-Konfiguration; null-Fall (kein localStorage) = Standard-Reihenfolge
  currentMetaConfig = () => {
    const { metaFields, metaConfig } = this.state;
    if (metaConfig) return metaConfig;
    return { hidden: new Set(), order: metaFields.map((f) => f.key) };
  };

  applyMetaConfig = (config) => {
    if (config) {
      writeTableViewConfig(this.props.apiEndpoint, config);
    }
    const metaColumns = this.attachMetaInlineEdit(
      this.props.apiEndpoint,
      buildMetaColumns(this.state.metaFields, config),
    );
    this.setState((prev) => ({
      metaConfig: config,
      columns: [
        ...prev.columns.filter((c) => !isMetaColumn(c)),
        ...metaColumns,
      ],
    }));
    // CSV-Query mitziehen: der Export muss die Ansicht auch ohne neuen fetchData
    // respektieren — Spaltenbasis ungescoopt (csvMetaFields), die FRISCHE config
    // (kein currentMetaConfig(): state.metaConfig ist hier noch der alte Wert)
    if (this.lastCsvQuery && typeof this.props.setCsvQuery === "function") {
      const csvCols = buildMetaColumns(
        this.state.csvMetaFields ?? [],
        config,
      ).map((c) => c.key);
      const csvQuery = {
        ...this.lastCsvQuery.query,
        ...(this.state.csvMetaFields?.length > 0
          ? { metaCols: csvCols.join(",") }
          : {}),
      };
      this.lastCsvQuery = {
        apiEndpoint: this.lastCsvQuery.apiEndpoint,
        query: csvQuery,
      };
      this.props.setCsvQuery(this.lastCsvQuery);
    }
  };

  toggleMetaColumn = (key) => {
    const config = this.currentMetaConfig();
    const hidden = new Set(config.hidden);
    if (hidden.has(key)) hidden.delete(key);
    else hidden.add(key);
    this.applyMetaConfig({ hidden, order: config.order });
  };

  moveMetaColumn = (key, delta) => {
    const config = this.currentMetaConfig();
    const idx = config.order.indexOf(key);
    const target = idx + delta;
    if (idx === -1 || target < 0 || target >= config.order.length) return;
    const order = [...config.order];
    [order[idx], order[target]] = [order[target], order[idx]];
    this.applyMetaConfig({ hidden: config.hidden, order });
  };

  moveMetaColumnTo = (fromKey, targetKey) => {
    const config = this.currentMetaConfig();
    const from = config.order.indexOf(fromKey);
    const to = config.order.indexOf(targetKey);
    if (from === -1 || to === -1 || from === to) return;
    const order = [...config.order];
    order.splice(from, 1);
    order.splice(to, 0, fromKey);
    this.applyMetaConfig({ hidden: config.hidden, order });
  };

  resetMetaColumns = () => {
    localStorage.removeItem(TABLE_VIEW_PREFIX + this.props.apiEndpoint);
    this.applyMetaConfig(null);
  };

  handleMetaDragStart = (e, key) => {
    e.dataTransfer.effectAllowed = "move";
    // Firefox startet Drag & Drop nur, wenn Daten gesetzt wurden
    e.dataTransfer.setData("text/plain", key);
    this.dragMetaKey = key;
  };

  handleMetaDragEnd = () => {
    this.dragMetaKey = null;
  };

  handleMetaDragOver = (e) => {
    if (this.dragMetaKey) e.preventDefault();
  };

  handleMetaDrop = (e, targetKey) => {
    e.preventDefault();
    const from = this.dragMetaKey;
    this.dragMetaKey = null;
    if (from && from !== targetKey) this.moveMetaColumnTo(from, targetKey);
  };

  renderColumnMenu() {
    const { metaFields, columnMenuOpen } = this.state;
    if (!metaFields || metaFields.length === 0) return null;
    const config = this.currentMetaConfig();
    const orderedFields = [
      ...config.order
        .map((k) => metaFields.find((f) => f.key === k))
        .filter(Boolean),
      ...metaFields.filter((f) => !config.order.includes(f.key)),
    ];
    return (
      <div
        className="table-view-toolbar"
        style={{ textAlign: "right", marginBottom: "0.5rem" }}
      >
        <Dropdown
          isOpen={columnMenuOpen}
          toggle={() =>
            this.setState((prev) => ({
              columnMenuOpen: !prev.columnMenuOpen,
            }))
          }
        >
          <DropdownToggle caret className="btn btn-sm btn-default">
            Spalten
          </DropdownToggle>
          <DropdownMenu right>
            {orderedFields.map((f, idx) => (
              <div
                key={f.key}
                className="dropdown-item d-flex align-items-center py-1"
              >
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={!config.hidden.has(f.key)}
                  onChange={() => this.toggleMetaColumn(f.key)}
                />
                <span className="flex-grow-1">{f.label}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-link px-1"
                  disabled={idx === 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    this.moveMetaColumn(f.key, -1);
                  }}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-link px-1"
                  disabled={idx === orderedFields.length - 1}
                  onClick={(e) => {
                    e.stopPropagation();
                    this.moveMetaColumn(f.key, 1);
                  }}
                >
                  ↓
                </button>
              </div>
            ))}
            <DropdownItem divider />
            <DropdownItem
              toggle={false}
              className="text-danger"
              onClick={this.resetMetaColumns}
            >
              Zurücksetzen
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>
    );
  }

  historyToggle = async (mitarbeiterId, karteiId) => {
    let headers = {
      headers: {
        "Content-Type": "application/json",
        Authorization: global.auth.authToken.token,
      },
    };
    let data = await fetch(
      global.api_url +
        `untersuchungen/hist?mitarbeiterId=${mitarbeiterId}&firmenkarteiId=${karteiId}`,
      headers,
    )
      .then((res) => res.json())
      .catch((err) => logger.log(err));

    this.setState({
      showHistoryOnly: !this.state.showHistoryOnly,
      historyData: data,
    });
  };
  toggleHistoryOnly = () => {
    this.setState({ showHistoryOnly: !this.state.showHistoryOnly });
  };

  toggleEmailLogDetail = (item = null) => {
    this.setState((prevState) => ({
      isEmailLogDetailOpen: !prevState.isEmailLogDetailOpen,
      emailLogDetail: item,
    }));
  };

  handleResendEmail = async (item, forceSender = false) => {
    try {
      const result = await saveApi(`emailLog/${item.id}/resend`, {
        forceSender,
      });

      if (result?.needsConfirmation) {
        this.setState({
          emailLogConfirm: {
            item,
            message: result.message,
            originalFrom: result.originalFrom,
            smtpUser: result.smtpUser,
          },
        });
        return;
      }

      toast.success(result?.message || "E-Mail wurde erneut gesendet.");
      this.setState({ emailLogConfirm: null });
      this.toggleEmailLogDetail();
      this.fetchData();
    } catch (err) {
      logger.error("Fehler beim erneuten Senden:", err);
      toast.error(
        err?.message || "E-Mail konnte nicht erneut gesendet werden.",
      );
    }
  };

  confirmResendEmail = () => {
    const { emailLogConfirm } = this.state;
    if (emailLogConfirm) {
      this.handleResendEmail(emailLogConfirm.item, true);
    }
  };

  cancelResendEmail = () => {
    this.setState({ emailLogConfirm: null });
  };

  handleSortChange = (orderBy, sortOrder) => {
    this.setState({ orderBy, sortOrder, currentPage: 1, data: [] }, () =>
      this.fetchData(),
    );
  };

  toggleConfirmDialog(id) {
    this.setState({
      isConfirmationDialogOpen: !this.state.isConfirmationDialogOpen,
      deleteRow: id,
    });
  }

  async deleteObject(_e) {
    this.toggleConfirmDialog();

    deleteApi(this.props.apiEndpoint, this.state.deleteRow)
      .then((response) => {
        // logger.log("delete:", response);
        toast.success(response.message, {});
        global.emitter.emit("REFRESH_TABLE_DATA");
      })
      .catch((err) => {
        logger.error("Error", err);
        toast.error(err.message, {});
      });
  }

  toggleArchiveDialog(id) {
    this.setState({
      isArchivDialogOpen: !this.state.isArchivDialogOpen,
      archivObject: id,
    });
  }
  archiveObject() {
    this.toggleArchiveDialog();
    toggleArchiveStatus(this.state.archivObject, true);
  }

  restoreObject(mitarbeiterId) {
    logger.log("restore: ", mitarbeiterId);
    toggleArchiveStatus(mitarbeiterId, false);
  }

  async csvExportMitarbeiter(mitarbeiterId) {
    logger.log("Exportien als Zip: ", mitarbeiterId);
    downloadMitarbeiterZip(mitarbeiterId);
  }

  renderEmailLogConfirmModal() {
    const { emailLogConfirm } = this.state;
    if (!emailLogConfirm) return null;

    return (
      <Modal isOpen={true} toggle={this.cancelResendEmail} size="md">
        <ModalHeader toggle={this.cancelResendEmail}>
          Absender bestätigen
        </ModalHeader>
        <ModalBody>
          <p>{emailLogConfirm.message}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={this.confirmResendEmail}>
            Mit {emailLogConfirm.smtpUser} senden
          </Button>{" "}
          <Button color="secondary" onClick={this.cancelResendEmail}>
            Abbrechen
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  renderEmailLogDetailModal() {
    const { emailLogDetail, isEmailLogDetailOpen } = this.state;
    if (!emailLogDetail) return null;

    return (
      <Modal
        isOpen={isEmailLogDetailOpen}
        toggle={() => this.toggleEmailLogDetail()}
        size="lg"
      >
        <ModalHeader toggle={() => this.toggleEmailLogDetail()}>
          E-Mail Details
        </ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-md-6">
              <p>
                <strong>Empfänger:</strong> {emailLogDetail.to}
              </p>
              <p>
                <strong>Absender:</strong> {emailLogDetail.from}
              </p>
              <p>
                <strong>Betreff:</strong> {emailLogDetail.subject || "-"}
              </p>
            </div>
            <div className="col-md-6">
              <p>
                <strong>Status:</strong>{" "}
                <span
                  className={`badge badge-${emailLogDetail.status === "sent" ? "success" : emailLogDetail.status === "failed" ? "danger" : "warning"}`}
                >
                  {emailLogDetail.status === "sent"
                    ? "Gesendet"
                    : emailLogDetail.status === "failed"
                      ? "Fehler"
                      : "Geplant"}
                </span>
              </p>
              <p>
                <strong>Auslöser:</strong>{" "}
                {emailLogDetail.trigger
                  ? {
                      comingUpExamination: "Anstehende Untersuchung",
                      angebotExamination: "Angebotsuntersuchung",
                      bereichExamination: "Bereichsuntersuchung",
                      otp: "OTP",
                      passwordReset: "Passwort-Reset",
                      testMail: "Test-Mail",
                    }[emailLogDetail.trigger] || emailLogDetail.trigger
                  : "-"}
              </p>
              <p>
                <strong>Gesendet am:</strong>{" "}
                {emailLogDetail.sendDateActual
                  ? moment(emailLogDetail.sendDateActual).format(
                      "DD.MM.YYYY HH:mm",
                    )
                  : "-"}
              </p>
              {emailLogDetail.messageId && (
                <p>
                  <strong>Message-ID:</strong> {emailLogDetail.messageId}
                </p>
              )}
            </div>
          </div>
          {emailLogDetail.error && (
            <div className="alert alert-danger mt-3">
              <strong>Fehler:</strong>
              <EmailLogError error={emailLogDetail.error} />
            </div>
          )}
          <hr />
          <div
            className="email-body-preview"
            dangerouslySetInnerHTML={{
              __html: emailLogDetail.bodyHtml || emailLogDetail.bodyText || "",
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            disabled={emailLogDetail.status === "queued"}
            onClick={() => this.handleResendEmail(emailLogDetail)}
          >
            Erneut senden
          </Button>{" "}
          <Button color="secondary" onClick={() => this.toggleEmailLogDetail()}>
            Schließen
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  render() {
    const { data, columns, loading, error, hasMore } = this.state;
    // An geladenen (nicht gerenderten) Meta-Feldern: Ausblenden ändert Sticky nicht
    const stickyActions = this.state.metaFields.length > 0;
    const {
      rowClicks,
      onRowEdit,
      apiEndpoint,
      embedded,
      archive,
      onNachuntersuchung,
      parentId,
    } = this.props;
    if (loading && data.length === 0) {
      return <LoadingSpinner show={loading} />;
    }

    if (error) {
      return <div>Error: {error.message}</div>;
    }

    let histModal = (
      <Modal
        isOpen={this.state.showHistoryOnly}
        toggle={this.toggleHistoryOnly}
        size="xl"
        centered
      >
        <ModalHeader toggle={this.toggleHistoryOnly}>Historie</ModalHeader>
        <ModalBody>
          {/* LoadingSpinner optional */}
          {!this.state.historyData || this.state.historyData.length === 0 ? (
            <p>Keine Historie vorhanden.</p>
          ) : (
            <HistoryList liste={this.state.historyData} />
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={this.toggleHistoryOnly}>
            Schließen
          </Button>
        </ModalFooter>
      </Modal>
    );

    return (
      <>
        {this.renderColumnMenu()}
        <table className="table table-striped " ref={this.scrollContainerRef}>
          <TableHeader
            key={embedded ? 11 : 1}
            columns={columns}
            order={this.state.orderBy}
            sort={this.state.sortOrder}
            handleSortChange={this.handleSortChange}
            metaSortable={apiEndpoint === MITARBEITER}
            stickyActions={stickyActions}
            onDragStart={this.handleMetaDragStart}
            onDragOver={this.handleMetaDragOver}
            onDragEnd={this.handleMetaDragEnd}
            onDrop={this.handleMetaDrop}
          />
          <TableBody
            key={embedded ? 12 : 2}
            embedded={embedded}
            data={data}
            rowClicks={rowClicks || false}
            columns={columns}
            stickyActions={stickyActions}
            onDelete={this.toggleConfirmDialog.bind(this)}
            onEdit={onRowEdit}
            onNachuntersuchung={onNachuntersuchung ?? false}
            onArchive={
              apiEndpoint === MITARBEITER
                ? this.toggleArchiveDialog.bind(this)
                : false
            }
            onRestore={archive ? this.restoreObject.bind(this) : false}
            onExport={
              apiEndpoint === MITARBEITER
                ? this.csvExportMitarbeiter.bind(this)
                : false
            }
            onHistory={this.historyToggle.bind(this)}
            parentId={parentId}
            is_archived={archive}
            filters={this.props.filters}
            apiEndpoint={this.props.apiEndpoint}
            onEmailLogDetail={
              apiEndpoint === "emailLog"
                ? this.toggleEmailLogDetail.bind(this)
                : false
            }
            onEmailLogResend={
              apiEndpoint === "emailLog"
                ? this.handleResendEmail.bind(this)
                : false
            }
          />
        </table>
        {!embedded && (
          <span
            className="info m-3"
            style={{ textAlign: "center", display: "block", color: "grey" }}
          >
            {loading && <LoadingSpinner show={loading} />}
            {hasMore ? "" : <span>Alle Elemente wurden geladen.</span>}
          </span>
        )}
        <ConfirmationDialog
          isOpen={this.state.isConfirmationDialogOpen}
          title={`Bestätigung erforderlich`}
          message={`Bist du sicher, dass du diesen Eintrag undwiederruflich löschen möchtest?`}
          onConfirm={this.deleteObject.bind(this)}
          onClose={this.toggleConfirmDialog.bind(this)}
        />
        <ArchiveDialog
          isOpen={this.state.isArchivDialogOpen}
          title={`Mitarbeiter archivieren`}
          message={`Möchten Sie den Mitarbeiter archivieren?`}
          buttons={[
            {
              label: "Abbrechen",
              onClick: this.toggleArchiveDialog.bind(this),
              color: "secondary",
            },
            {
              label: "Archivieren",
              onClick: this.archiveObject.bind(this),
              color: "primary",
            },
          ]}
          onClose={this.toggleArchiveDialog.bind(this)}
        />
        {histModal}
        {this.renderEmailLogDetailModal()}
        {this.renderEmailLogConfirmModal()}
      </>
    );
  }
}

Table.propTypes = {
  apiEndpoint: PropTypes.string.isRequired,
  rowClicks: PropTypes.any,
  filters: PropTypes.object,
  orderBy: PropTypes.string,
  sortOrder: PropTypes.string,
  onRowEdit: PropTypes.func.isRequired,
  setCsvQuery: PropTypes.func,
  embedded: PropTypes.bool,
  onNachuntersuchung: PropTypes.func,
  archive: PropTypes.bool,
  onMetaFilterFields: PropTypes.func,
  parentId: PropTypes.any,
};

Table.defaultProps = {
  rowClicks: false,
  embedded: false,
  parentId: false,
};

export default Table;
